import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { UserInterface } from 'src/app/models/user.interface';
import { UserService } from 'src/app/core/services/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { formatRut, validateRut } from 'rutlib';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LoggerService } from 'src/app/core/services/logger.service';

function validarRUT(control: AbstractControl) {
  const rut = control.value;
  if (rut) {
    if (!validateRut(rut)) {
      return { rutInvalido: 'Ingresa un RUT válido' };
    }
  }
  return null;
}

@Component({
  selector: 'globot-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss'],
})
export class SubscriptionsComponent implements OnInit {
  @Input() user?: UserInterface;
  noRut: boolean = false;
  subscriptionForm: FormGroup;
  planType: string = '';
  planPrice: string = '';
  countries = [
    { name: 'Chile', flag: '🇨🇱', dial_code: '56' },
    { name: 'Argentina', flag: '🇦🇷', dial_code: '54' },
    { name: 'Brasil', flag: '🇧🇷', dial_code: '55' },
    { name: 'Colombia', flag: '🇨🇴', dial_code: '57' },
    { name: 'Perú', flag: '🇵🇪', dial_code: '51' },
    { name: 'Venezuela', flag: '🇻🇪', dial_code: '58' },
  ];

  selectedCountry: string = '';
  loading: boolean = false;
  termsUrl = environment.termsUrl;
  privacyUrl = environment.privacyUrl;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private readonly ms: MessageService,
    private logger: LoggerService
  ) {
    this.subscriptionForm = this.fb.group({
      email: [''],
      name_on_invoice: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          this.validarFormatoNombre,
        ],
      ],
      document_value: ['', [Validators.required, validarRUT]],
      phone: [
        '',
        [
          Validators.required,
          Validators.minLength(9),
          Validators.maxLength(13),
          this.validarTelefono,
        ],
      ],
      type_document: ['DNI'],
      country: ['56'],
      acceptTerms: [false, Validators.requiredTrue],
    });
    this.selectedCountry = this.subscriptionForm.controls['country'].value;
  }

  goBack(): void {
    this.router.navigate(['/dashboard/edit-user'], { queryParams: { tab: 'plan' } });
  }

  ngOnInit() {
    this.userService.getUser().subscribe((user) => {
      this.subscriptionForm.controls['email'].setValue(user['email']);

      if (user['name_on_invoice']) {
        this.subscriptionForm.controls['name_on_invoice'].setValue(user['name_on_invoice']);
      }

      if (user['document_value']) {
        this.subscriptionForm.controls['document_value'].setValue(formatRut(user['document_value']));
      }

      if (user['phone_number']) {
        const phone: string = user['phone_number'];
        const matchedCountry = this.countries.find((c) => phone.startsWith(c.dial_code));
        if (matchedCountry) {
          this.subscriptionForm.controls['country'].setValue(matchedCountry.dial_code);
          this.selectedCountry = matchedCountry.dial_code;
          this.subscriptionForm.controls['phone'].setValue(phone.slice(matchedCountry.dial_code.length));
        } else {
          this.subscriptionForm.controls['phone'].setValue(phone);
        }
      }
    });

    this.route.queryParamMap.subscribe((params) => {
      const typeEncoded = params.get('subscriptionPlan');
      if (!typeEncoded) {
        return;
      }
      try {
        const decodedType = atob(typeEncoded);
        if (!decodedType) {
          return;
        }
        const type = decodedType;
        const planTypeMap: { [key: string]: string } = {
          BASE: 'Standard',
          PREMIUM: 'Premium',
          TEST: 'Plan Prueba'
        };
        this.planType = planTypeMap[type] || '';
        const planPriceMap: { [key: string]: string } = {
          BASE: '12.990',
          PREMIUM: '29.990',
          TEST: '1.000'
        };
        this.planPrice = planPriceMap[type] || '';
      } catch (error) {
        // Invalid base64 string, skip
        return;
      }
    });
  }

  validarTelefono(control: AbstractControl) {
    const telefono = control.value;
    const regexTelefono = /^\d{9,11}$/;
    if (!regexTelefono.test(telefono)) {
      return { telefonoInvalido: 'Ingresa un teléfono válido' };
    }
    return null;
  }

  checkboxRut(event: { checked: boolean }) {
    this.noRut = event.checked;
    if (this.noRut) {
      // Si no tiene RUT, eliminar las validaciones del campo
      this.subscriptionForm.controls['document_value'].setValue(null);
      this.subscriptionForm.get('document_value')?.clearValidators();
      this.subscriptionForm.get('document_value')?.updateValueAndValidity(); // Refrescar validaciones
    } else {
      // Si tiene RUT, agregar las validaciones de nuevo
      this.subscriptionForm.get('document_value')?.setValidators([Validators.required, validarRUT]);
      this.subscriptionForm.get('document_value')?.updateValueAndValidity(); // Refrescar validaciones
    }
  }

  onSubmit() {
    if (this.subscriptionForm.valid) {
      this.loading = true;

      let formValues = this.subscriptionForm.value;
      formValues.document_value = !this.noRut
        ? formValues.document_value.replace(/[.-]/g, '')
        : null;
      formValues.phone = this.selectedCountry + formValues.phone;
      this.userService.updateUser(this.subscriptionForm.value).subscribe({
        next: (_updatedUser) => {
          let subType = this.route.snapshot.queryParamMap.get('subscriptionPlan') || '';
          this.userService.createSubscription(subType, subType).subscribe({
            next: (response: any) => {
              const urlWebpay = response.data.url_webpay;
              const tbkToken = response.data.token;
              if (urlWebpay && tbkToken) {
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = urlWebpay;
                const tokenInput = document.createElement('input');
                tokenInput.type = 'hidden';
                tokenInput.name = 'TBK_TOKEN';
                tokenInput.value = tbkToken;
                form.appendChild(tokenInput);
                document.body.appendChild(form);
                form.submit();
              }
            },
            error: (error: HttpErrorResponse) => {
              this.loading = false;
              if (error.status === 400 && error.error && error.error.code === 8) {
                this.ms.add({
                  severity: 'error',
                  summary: 'Acción no permitida',
                  detail: 'Ya te suscribiste a este plan',
                });
              } else {
                this.ms.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Ha ocurrido un error al crear la suscripción',
                });
              }
              this.logger.error('Error creating subscription', error);
            },
          });
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          this.ms.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Ha ocurrido un error al actualizar el usuario',
          });
          this.logger.error('Error updating user', error);
        },
      });
    }
  }
  onblur() {
    const rut = this.subscriptionForm.get('document_value')?.value;
    this.subscriptionForm.controls['document_value'].setValue(formatRut(rut));
  }
  getCodeNumber(): string {
    const code = this.subscriptionForm.get('country')?.value;

    return '+(' + code + ')';
  }

  onCountryChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedCountry = selectElement.value;
  }

  validarFormatoNombre(control: AbstractControl) {
    const nombre = control.value;
    const regexNombre = /^[A-Za-zÀ-ÿ]+([ '-][A-Za-zÀ-ÿ]+)*$/;
    if (!regexNombre.test(nombre)) {
      return { formatoInvalido: 'Ingresa un nombre y apellido válido' };
    }
    return null;
  }

  getInputClass(controlName: string) {
    return {
      'invalid-input':
        this.subscriptionForm.controls[controlName].invalid &&
        this.subscriptionForm.controls[controlName].touched,
    };
  }
  getErrorMessage(field: string) {
    let control = this.subscriptionForm.get(field);

    if (control?.errors) {
      if (control.hasError('required')) {
        return 'Campo requerido';
      } else if (control.hasError('rutInvalido')) {
        return control.errors['rutInvalido'];
      } else if (control.hasError('telefonoInvalido')) {
        return control.errors['telefonoInvalido'];
      }
      return '';
    }
  }
  isFormValid(): boolean {
    // Verifica si el formulario es válido
    if (!this.subscriptionForm.valid) {
      return false;
    }

    // Verifica si hay algún campo vacío
    for (const controlName in this.subscriptionForm.controls) {
      if (this.subscriptionForm.controls.hasOwnProperty(controlName)) {
        const control = this.subscriptionForm.controls[controlName];

        // Condición especial: Si el campo es document_value y noRut es true, no debe bloquear la validación
        if (controlName === 'document_value' && this.noRut) {
          continue; // Salta la validación de este campo si noRut es true
        }

        // Si hay algún campo vacío, retornar false
        if (control.value === null || control.value === '') {
          return false;
        }
      }
    }
    return true;
  }
}
